import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button, Typography, Box, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip } from '@mui/material';
import { Menu as MenuIcon, ArrowBack, Phone, Email } from '@mui/icons-material';
import ThemeBackground from '../Layout/ThemeBackground';
import MenuDrawer from '../Mobile/MenuDrawer';
import BottomNav from '../Mobile/BottomNav';
import PersonalInformation from './PersonalInformation';
import { CounsellingInformation } from './CounsellingInformation';
import PaymentSessionHistory from './PaymentSessionHistory';
import SimpleReviewHistory from '../Reviews/SimpleReviewHistory';
import HistoricalNotesControl from './HistoricalNotesControl';
import ClientAuditTimeline from './ClientAuditTimeline';
import { Client, Episode } from '../../types/client';
import { todayDateOnlyString } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';

interface Lookup {
  _id: string;
  category: string;
  value: string;
  displayName: string;
  active: boolean;
  sessionRate?: number;
  numberOfSessions?: number;
}

interface LookupState {
  gender: Lookup[];
  availability: Lookup[];
  sessionTypes: Lookup[];
}

const ClientDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [client, setClient] = React.useState<Client | null>(null);
  const [lookups, setLookups] = React.useState<LookupState>({
    gender: [],
    availability: [],
    sessionTypes: []
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isEditingPersonal, setIsEditingPersonal] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);
  const [isReissuingTerms, setIsReissuingTerms] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const [activeTab, setActiveTab] = React.useState<'personal' | 'counselling' | 'sessions' | 'payments' | 'reviews' | 'audit'>('personal');
  const [hasReviewedContract, setHasReviewedContract] = React.useState(false);
  const [isReviewingContract, setIsReviewingContract] = React.useState(false);
  const [isAssignedCounsellor, setIsAssignedCounsellor] = React.useState(false);
  const [isSavingContract, setIsSavingContract] = React.useState(false);
  const [gender, setGender] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [street, setStreet] = React.useState('');
  const [addressLine2, setAddressLine2] = React.useState('');
  const [addressLine3, setAddressLine3] = React.useState('');
  const [city, setCity] = React.useState('');
  const [postcode, setPostcode] = React.useState('');
  const [county, setCounty] = React.useState('');
  const [country, setCountry] = React.useState('');
  const [emergencyContactName, setEmergencyContactName] = React.useState('');
  const [emergencyContactPhone, setEmergencyContactPhone] = React.useState('');
  const [emergencyContactRelationship, setEmergencyContactRelationship] = React.useState('');
  const [emergencyContacts, setEmergencyContacts] = React.useState<Array<{name: string; phone: string; relationship: string}>>([]);
  const [gpPractice, setGpPractice] = React.useState('');
  const [gpAddress, setGpAddress] = React.useState('');
  const [gpAddressLine2, setGpAddressLine2] = React.useState('');
  const [gpAddressLine3, setGpAddressLine3] = React.useState('');
  const [gpCity, setGpCity] = React.useState('');
  const [gpCounty, setGpCounty] = React.useState('');
  const [gpPostcode, setGpPostcode] = React.useState('');
  const [gpCountry, setGpCountry] = React.useState('');
  const [gpPhone, setGpPhone] = React.useState('');
  const [, setDay] = React.useState('');
  const [, setMonth] = React.useState('');
  const [, setYear] = React.useState('');
  const [selectedAvailability, setSelectedAvailability] = React.useState<string[]>([]);
  const [availabilitySlots, setAvailabilitySlots] = React.useState<Array<{ day: string; timeRanges: Array<{ start: string; end: string }> }>>([]);
  const [hasAppointments, setHasAppointments] = React.useState(false);
  const [selectedSessionTypes, setSelectedSessionTypes] = React.useState<string[]>([]);
  const [episodes, setEpisodes] = React.useState<Episode[]>([]);
  const [episodesLoading, setEpisodesLoading] = React.useState(false);
  const [episodesError, setEpisodesError] = React.useState<string | null>(null);
  const [isRecontractOpen, setIsRecontractOpen] = React.useState(false);
  const [isRecontracting, setIsRecontracting] = React.useState(false);
  const [sessionRates, setSessionRates] = React.useState<Lookup[]>([]);
  const [selectedSessionRate, setSelectedSessionRate] = React.useState('');
  const [referralTypes, setReferralTypes] = React.useState<Lookup[]>([]);
  const [selectedReferralType, setSelectedReferralType] = React.useState('');
  const [recontractCategory, setRecontractCategory] = React.useState<'direct' | 'organisation'>('direct');
  const [recontractOverrideReason, setRecontractOverrideReason] = React.useState('');
  const [reviewInviteBusy, setReviewInviteBusy] = React.useState(false);
  const [reviewInviteMsg, setReviewInviteMsg] = React.useState('');
  const [organisations, setOrganisations] = React.useState<Lookup[]>([]);
  const [selectedOrganisation, setSelectedOrganisation] = React.useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [clientActiveDialog, setClientActiveDialog] = React.useState(false);
  const [inactiveClientDialog, setInactiveClientDialog] = React.useState(false);
  const [clientLeftDate, setClientLeftDate] = React.useState('');
  const [isArchivingClient, setIsArchivingClient] = React.useState(false);
  const navigate = useNavigate();

  const handleError = (error: any) => {
    console.error('Error:', error);
    if (error.response) {
      // Server responded with an error status
      if (error.response.status === 503) {
        setError('The server is currently unavailable. Please try again in a few minutes.');
      } else {
        setError(`Server Error: ${error.response.status} - ${error.response.statusText}`);
      }
    } else if (error.request) {
      // Request was made but no response received
      setError('Unable to connect to the server. Please check your internet connection.');
    } else {
      // Error in request setup
      setError('An error occurred while processing your request.');
    }
    setLoading(false);
  };

  const retryFetch = () => {
    setLoading(true);
    setError(null);
    setRetryCount(prev => prev + 1);
  };

  const handleUpdateClient = async (updates: Partial<Client>) => {
    if (!client) return;
    
    const processedUpdates = Object.entries(updates).reduce<Record<string, unknown>>((acc, [key, value]) => {
      // Skip organisation field if it's empty
      if (key === 'organisation' && !value) {
        return acc;
      }
      acc[key] = value instanceof Date ? value.toISOString() : value;
      return acc;
    }, {});

    const updatedClient: Client = {
      ...client,
      ...processedUpdates,
      counsellorHistory: client.counsellorHistory ?? []
    };

    try {
      // Save to backend
      const response = await api.put(`/api/clients/${id}`, updatedClient);
      // Update local state with the response data
      setClient(response.data);
      setError(null);
    } catch (error) {
      console.error('Error updating client:', error);
      setError('Failed to update client information');
      // Revert local state if backend update fails
      setClient(client);
    }
  };

  React.useEffect(() => {
    const fetchClientDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch appointments to check if any scheduled appointments exist
        try {
          const appointmentsResponse = await api.get(`/api/appointments/client/${id}`);
          // Only count scheduled appointments (not completed/cancelled)
          const scheduledAppointments = appointmentsResponse.data?.filter(
            (apt: any) => apt.status === 'scheduled'
          ) || [];
          setHasAppointments(scheduledAppointments.length > 0);
        } catch (error) {
          console.error('Error fetching appointments:', error);
          setHasAppointments(false);
        }
        
        if (!id) {
          setError('No client ID provided');
          setLoading(false);
          return;
        }

        const [clientResponse, genderLookups, availabilityLookups, sessionTypeLookups] = await Promise.all([
          api.get(`/api/clients/${id}`),
          api.get('/api/lookups/admin/gender'),  
          api.get('/api/lookups/public/availability'),
          api.get('/api/lookups/public/sessionTypes')
        ]);

        setClient(clientResponse.data);

        const lookupState = {
          gender: genderLookups.data,
          availability: availabilityLookups.data,
          sessionTypes: sessionTypeLookups.data
        };
        setLookups(lookupState);
        setLoading(false);
      } catch (error: any) {
        console.error('Error in fetchClientDetails:', {
          error,
          response: error.response?.data,
          status: error.response?.status,
          message: error.message
        });
        handleError(error);
      }
    };

    fetchClientDetails();
  }, [id, retryCount]);

  React.useEffect(() => {
    if (client) {
      // Initialize personal information form state
      setGender(client.gender || '');
      setAddress(client.address || '');
      
      // Initialize emergency contact and GP information
      setEmergencyContactName(client.emergencyContactName || '');
      setEmergencyContactPhone(client.emergencyContactPhone || '');
      setEmergencyContactRelationship(client.emergencyContactRelationship || '');
      setGpPractice(client.gpPractice || '');
      setGpAddress(client.gpAddress || '');
      setGpAddressLine2(client.gpAddressLine2 || '');
      setGpAddressLine3(client.gpAddressLine3 || '');
      setGpCity(client.gpCity || '');
      setGpCounty(client.gpCounty || '');
      setGpPostcode(client.gpPostcode || '');
      setGpCountry(client.gpCountry || '');
      setGpPhone(client.gpPhone || '');
      
      // Initialize date of birth
      if (client.dob) {
        const dobDate = new Date(client.dob);
        setDay(dobDate.getDate().toString().padStart(2, '0'));
        setMonth((dobDate.getMonth() + 1).toString().padStart(2, '0'));
        setYear(dobDate.getFullYear().toString());
      }
      
      // Initialize counselling information
      setSelectedAvailability(client.availability || []);
      setAvailabilitySlots(client.availabilitySlots || []);
      setSelectedSessionTypes(client.sessionTypes || []);

      // Check contract review status for counsellors
      if (user?.role === 'counsellor' && client.currentCounsellorId) {
        // Fetch counsellor profile to get the counsellor ID
        const fetchCounsellorId = async () => {
          try {
            const profileResponse = await api.get('/api/counsellors/profile');
            const counsellorId = profileResponse.data._id;
            const isCounsellorAssigned = client.currentCounsellorId === counsellorId;
            setIsAssignedCounsellor(isCounsellorAssigned);
            
            if (isCounsellorAssigned && client.contractReviewedBy) {
              const hasReviewed = client.contractReviewedBy.some(
                (review: any) => review.counsellorId === counsellorId
              );
              setHasReviewedContract(hasReviewed);
            } else {
              setHasReviewedContract(false);
            }
          } catch (error) {
            console.error('Error fetching counsellor profile:', error);
            setIsAssignedCounsellor(false);
            setHasReviewedContract(false);
          }
        };
        fetchCounsellorId();
      } else {
        setIsAssignedCounsellor(false);
        setHasReviewedContract(true); // Admins don't need to review
      }

      setLoading(false);
    }
  }, [client, user]);

  const handleReissueTerms = async () => {
    try {
      setIsReissuingTerms(true);
      if (!client?.termsIssued) {
        // If terms haven't been issued yet, generate them first
        await api.post(`/api/contract/generate-pdf/${id}`);
      } else {
        // If terms have been issued, reissue them
        await api.post(`/api/contract/reissue-terms/${id}`);
      }
      
      // Refresh client data to get updated terms status
      const response = await api.get(`/api/clients/${id}`);
      console.log('Client state after reissuing terms:', response.data);
      
      // Validate the response data
      if (!response.data.contractToken || !response.data.contractTokenExpiry) {
        throw new Error('Invalid response: Missing contract token or expiry date');
      }
      
      // Validate that the expiry date is in the future
      const expiryDate = new Date(response.data.contractTokenExpiry);
      const currentDate = new Date();
      if (expiryDate <= currentDate) {
        throw new Error('Invalid response: Contract token expiry date is not in the future');
      }
      
      setClient(response.data);
      setSnackbar({
        open: true,
        message: 'Terms have been issued successfully',
        severity: 'success'
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to issue terms';
      console.error('Error with terms:', error.response?.data || error);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsReissuingTerms(false);
    }
  };

  const handleSessionNotes = () => {
    navigate(`/clients/${id}/sessions`);
  };

  const handleOpenContractReview = () => {
    setIsReviewingContract(true);
  };

  const handleCloseContractReview = () => {
    setIsReviewingContract(false);
  };

  const handleAcknowledgeContract = async () => {
    try {
      setIsSavingContract(true);
      await api.post(`/api/clients/${id}/review-contract`);
      setHasReviewedContract(true);
      setIsReviewingContract(false);
      setSnackbar({
        open: true,
        message: 'Contract reviewed successfully',
        severity: 'success'
      });
      // Refresh client details to get updated status
      const response = await api.get(`/api/clients/${id}`);
      setClient(response.data);
    } catch (error: any) {
      console.error('Error reviewing contract:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Error reviewing contract',
        severity: 'error'
      });
    } finally {
      setIsSavingContract(false);
    }
  };

  const fetchEpisodes = React.useCallback(async () => {
    if (!id) return;
    
    try {
      setEpisodesLoading(true);
      setEpisodesError(null);
      
      const response = await api.get(`/api/episodes?clientId=${id}`);
      setEpisodes(response.data);
    } catch (error: any) {
      console.error('Error fetching episodes:', error);
      setEpisodesError('Failed to load episodes');
    } finally {
      setEpisodesLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    if (client?.termsAccepted) {
      fetchEpisodes();
    }
  }, [client?.termsAccepted, fetchEpisodes]);

  const createEpisode = async (episodeData: Partial<Episode>) => {
    try {
      const response = await api.post('/api/episodes', {
        ...episodeData,
        clientId: id
      });
      
      setEpisodes(prev => [...prev, response.data]);
      return response.data;
    } catch (error: any) {
      console.error('Error creating episode:', error);
      throw error;
    }
  };


  // Queues an approval-required review invitation - the actual email only
  // sends when an admin approves it on the Utilisation > Automation tab.
  const handleReviewInvite = async () => {
    setReviewInviteBusy(true);
    setReviewInviteMsg('');
    try {
      await api.post('/api/admin/verified-reviews/invitations', { clientId: id });
      setReviewInviteMsg('Review invitation queued - approve it on the Automation tab to send');
    } catch (err: any) {
      setReviewInviteMsg(err?.response?.data?.error || 'Could not create review invitation');
    } finally {
      setReviewInviteBusy(false);
    }
  };

  const handleOpenRecontract = async () => {
    try {
      const [ratesResponse, referralTypesResponse, orgsResponse] = await Promise.all([
        api.get('/api/lookups/admin/sessionRates'),
        api.get('/api/lookups/admin/referralTypes'),
        api.get('/api/lookups/admin/organisations')
      ]);
      const rates = Array.isArray(ratesResponse.data) ? ratesResponse.data : (ratesResponse.data?.sessionRates || []);
      setSessionRates(rates.filter((r: Lookup) => r.active));
      const types = Array.isArray(referralTypesResponse.data) ? referralTypesResponse.data : (referralTypesResponse.data?.referralTypes || []);
      setReferralTypes(types.filter((t: Lookup) => t.active));
      const orgs = Array.isArray(orgsResponse.data) ? orgsResponse.data : (orgsResponse.data?.organisations || []);
      setOrganisations(orgs.filter((o: Lookup) => o.active));
      setSelectedSessionRate('');
      setSelectedReferralType('');
      setRecontractCategory('direct');
      setSelectedOrganisation('');
      setRecontractOverrideReason('');
      setIsRecontractOpen(true);
    } catch (error) {
      console.error('Error fetching recontract lookups:', error);
      setSnackbar({ open: true, message: 'Failed to load recontract options', severity: 'error' });
    }
  };

  // Phase 6 (RECONTRACT-04): mirror of the server-side eligibility rule -
  // allocated sessions exhausted/approaching, or the counsellor recommended
  // continuation. Anything else requires an administrator override reason.
  const recontractAutoEligible =
    (client?.referralCategory === 'organisation' &&
      !!client?.numberOfSessions &&
      (client.numberOfSessions - (client.completedSessions || 0)) <= 1) ||
    (client?.continuationRequested === true && client?.continuationRequestedAction === 'recontract');

  const handleRecontract = async () => {
    if (recontractCategory === 'direct' && !selectedSessionRate) {
      setSnackbar({ open: true, message: 'Please select a session rate', severity: 'error' });
      return;
    }
    if (recontractCategory === 'organisation' && !selectedOrganisation) {
      setSnackbar({ open: true, message: 'Please select an organisation', severity: 'error' });
      return;
    }
    if (!recontractAutoEligible && !recontractOverrideReason.trim()) {
      setSnackbar({ open: true, message: 'This client is not automatically eligible - please record an administrator override reason', severity: 'error' });
      return;
    }
    try {
      setIsRecontracting(true);
      const payload: any = { referralCategory: recontractCategory };
      if (recontractCategory === 'direct') {
        payload.sessionRate = selectedSessionRate;
        payload.referralType = selectedReferralType || 'direct';
      } else {
        payload.organisationId = selectedOrganisation;
      }
      if (!recontractAutoEligible) {
        payload.adminOverride = true;
        payload.overrideReason = recontractOverrideReason.trim();
      }
      const response = await api.post(`/api/clients/${id}/recontract`, payload);
      setClient(response.data.client);
      setIsRecontractOpen(false);
      setSnackbar({ open: true, message: 'Client recontracted successfully. They now need a new assessment and contract.', severity: 'success' });
    } catch (error: any) {
      console.error('Error recontracting client:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to recontract client',
        severity: 'error'
      });
    } finally {
      setIsRecontracting(false);
    }
  };

  const handleCompleteOnboardingClick = () => {
    setClientActiveDialog(true);
  };

  const handleClientStillActive = () => {
    setClientActiveDialog(false);
    navigate(`/existing-client-onboarding/${id}`);
  };

  const handleClientNotActive = () => {
    setClientActiveDialog(false);
    setInactiveClientDialog(true);
  };

  const handleArchiveInactiveClient = async () => {
    if (!clientLeftDate) {
      setSnackbar({ open: true, message: 'Please select the date the client left', severity: 'error' });
      return;
    }

    const leftDate = new Date(clientLeftDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (leftDate > today) {
      setSnackbar({ open: true, message: 'Date cannot be in the future', severity: 'error' });
      return;
    }

    try {
      setIsArchivingClient(true);
      // Use existing continuation-request endpoint with wantsToContinue: false
      // This will set status to pending_archive and unassign the counsellor
      await api.post(`/api/clients/${id}/continuation-request`, {
        wantsToContinue: false
      });
      setInactiveClientDialog(false);
      setSnackbar({ 
        open: true, 
        message: 'Client has been marked as Pending Archive and removed from your client list.', 
        severity: 'success' 
      });
      setTimeout(() => navigate('/counsellor-dashboard'), 2000);
    } catch (error: any) {
      console.error('Error archiving inactive client:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to archive client',
        severity: 'error'
      });
    } finally {
      setIsArchivingClient(false);
    }
  };

  const handleReturnToCounsellor = async () => {
    if (!window.confirm(`Return ${client?.firstName} to their previous counsellor? This restores the counsellor assignment and sets the client back to Active.`)) return;
    try {
      const response = await api.post(`/api/clients/${id}/return-to-counsellor`);
      setClient(response.data.client);
      setSnackbar({
        open: true,
        message: 'Client returned to counsellor. The counsellor has been notified.',
        severity: 'success'
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to return client to counsellor',
        severity: 'error'
      });
    }
  };

  const renderContent = () => {
    if (loading) {
      return <Typography>Loading...</Typography>;
    }

    if (error) {
      return (
        <>
          <Typography color="error">{error}</Typography>
          <Button
            variant="contained"
            onClick={retryFetch}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              minWidth: '160px',
              backgroundColor: '#2196f3',
              '&:hover': {
                backgroundColor: '#1976d2'
              }
            }}
          >
            Retry
          </Button>
        </>
      );
    }

    if (!client) {
      return <Typography>No client data found</Typography>;
    }
    const isCounsellor = user?.role === 'counsellor';

    return (
      <>
      {/* Mobile View */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, pb: '80px' }}>
        {/* Mobile Header */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            background: 'rgba(26, 26, 46, 0.95)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            px: 2,
            py: 2,
            borderRadius: '0 0 16px 16px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <IconButton
                onClick={() => navigate(isCounsellor ? '/counsellor/dashboard' : '/clients')}
                sx={{
                  color: 'white',
                  p: 1
                }}
              >
                <ArrowBack />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}
              >
                {client?.firstName} {client?.lastName}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {client?.phone && (
                <IconButton
                  component="a"
                  href={`tel:${client.phone}`}
                  sx={{
                    color: 'white',
                    background: 'rgba(16, 185, 129, 0.2)',
                    borderRadius: '10px',
                    width: 44,
                    height: 44,
                    '&:hover': {
                      background: 'rgba(16, 185, 129, 0.3)',
                    }
                  }}
                >
                  <Phone fontSize="small" />
                </IconButton>
              )}
              {client?.email && (
                <IconButton
                  component="a"
                  href={`mailto:${client.email}`}
                  sx={{
                    color: 'white',
                    background: 'rgba(33, 150, 243, 0.2)',
                    borderRadius: '10px',
                    width: 44,
                    height: 44,
                    '&:hover': {
                      background: 'rgba(33, 150, 243, 0.3)',
                    }
                  }}
                >
                  <Email fontSize="small" />
                </IconButton>
              )}
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{
                  color: 'white',
                  background: 'rgba(102, 126, 234, 0.2)',
                  borderRadius: '10px',
                  width: 44,
                  height: 44,
                  '&:hover': {
                    background: 'rgba(102, 126, 234, 0.3)',
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Mobile Counsellor Status Card */}
        {client && (
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Box sx={{ 
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              p: 2,
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem' }}>
                  Counsellor
                </Typography>
                {client.assignedCounsellorName ? (
                  <Chip
                    label={client.assignedCounsellorName}
                    size="small"
                    sx={{
                      backgroundColor: '#4caf50',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: '28px'
                    }}
                  />
                ) : (
                  <Chip
                    label="Not Assigned"
                    size="small"
                    sx={{
                      backgroundColor: '#ff9800',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: '28px'
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Mobile Tabs */}
        <Box sx={{ px: 2, pt: 1, pb: 2 }}>
          <Box sx={{ 
            background: 'rgba(102, 126, 234, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            p: 1,
            border: '1px solid rgba(102, 126, 234, 0.2)',
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' }
          }}>
            {['personal', 'counselling', 'sessions', 'reviews', 'audit'].map((tab) => (
              <Box
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                sx={{
                  flex: 1,
                  minWidth: 'fit-content',
                  py: 1.5,
                  px: 2.5,
                  textAlign: 'center',
                  background: activeTab === tab ? 'rgba(102, 126, 234, 0.3)' : 'transparent',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    background: activeTab === tab ? 'rgba(102, 126, 234, 0.3)' : 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: activeTab === tab ? 'white' : 'rgba(255, 255, 255, 0.6)',
                    fontWeight: activeTab === tab ? 700 : 500,
                    fontSize: '0.75rem',
                    textTransform: 'capitalize',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {tab}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Mobile Content - Tab-based */}
        <Box sx={{ px: 2, py: 2 }}>
          {/* Personal Tab */}
          {activeTab === 'personal' && client && (
            <PersonalInformation
              client={client}
              lookups={lookups}
              onUpdate={handleUpdateClient}
              isEditing={isEditingPersonal}
              setIsEditing={setIsEditingPersonal}
              address={address}
              setAddress={setAddress}
              street={street}
              setStreet={setStreet}
              addressLine2={addressLine2}
              setAddressLine2={setAddressLine2}
              addressLine3={addressLine3}
              setAddressLine3={setAddressLine3}
              city={city}
              setCity={setCity}
              postcode={postcode}
              setPostcode={setPostcode}
              county={county}
              setCounty={setCounty}
              country={country}
              setCountry={setCountry}
              emergencyContactName={emergencyContactName}
              setEmergencyContactName={setEmergencyContactName}
              emergencyContactPhone={emergencyContactPhone}
              setEmergencyContactPhone={setEmergencyContactPhone}
              emergencyContactRelationship={emergencyContactRelationship}
              setEmergencyContactRelationship={setEmergencyContactRelationship}
              emergencyContacts={emergencyContacts}
              setEmergencyContacts={setEmergencyContacts}
              gpPractice={gpPractice}
              setGpPractice={setGpPractice}
              gpAddress={gpAddress}
              setGpAddress={setGpAddress}
              gpAddressLine2={gpAddressLine2}
              setGpAddressLine2={setGpAddressLine2}
              gpAddressLine3={gpAddressLine3}
              setGpAddressLine3={setGpAddressLine3}
              gpCity={gpCity}
              setGpCity={setGpCity}
              gpCounty={gpCounty}
              setGpCounty={setGpCounty}
              gpPostcode={gpPostcode}
              setGpPostcode={setGpPostcode}
              gpCountry={gpCountry}
              setGpCountry={setGpCountry}
              gpPhone={gpPhone}
              setGpPhone={setGpPhone}
              gender={gender}
              setGender={setGender}
              selectedAvailability={selectedAvailability}
              setSelectedAvailability={setSelectedAvailability}
              availabilitySlots={availabilitySlots}
              setAvailabilitySlots={setAvailabilitySlots}
              selectedSessionTypes={selectedSessionTypes}
              setSelectedSessionTypes={setSelectedSessionTypes}
            />
          )}

          {/* Counselling Tab */}
          {activeTab === 'counselling' && client && (
            <>
              <CounsellingInformation
                client={client}
                episodes={episodes}
                episodesLoading={episodesLoading}
                episodesError={episodesError}
                createEpisode={createEpisode}
              />
              
              {/* Historical Notes Control - Admin Only */}
              {(() => {
                console.log('Historical Notes Debug:', {
                  isCounsellor,
                  userRole: user?.role,
                  shouldShow: !isCounsellor,
                  clientId: client._id
                });
                return !isCounsellor && (
                  <HistoricalNotesControl
                    clientId={client._id}
                    allowHistoricalNotes={client.allowHistoricalNotes || false}
                    historicalNotesDeadline={client.historicalNotesDeadline}
                    historicalNotesCompletedFor={client.historicalNotesCompletedFor}
                    onUpdate={() => setRetryCount(prev => prev + 1)}
                  />
                );
              })()}
            </>
          )}

          {/* Sessions Tab */}
          {activeTab === 'sessions' && client && (
            <Box>
              {/* Action Buttons for Sessions */}
              <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {isAssignedCounsellor && client?.contractUrl && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleOpenContractReview}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      py: 1.5,
                      backgroundColor: hasReviewedContract ? '#9c27b0' : '#667eea',
                      '&:hover': {
                        backgroundColor: hasReviewedContract ? '#7b1fa2' : '#5568d3'
                      }
                    }}
                  >
                    {hasReviewedContract ? 'View Contract' : 'Review Contract'}
                  </Button>
                )}

                {(
                  (isCounsellor && client?.currentCounsellorId && !client?.offboarded && (client?.existingClientOnboarded || client?.termsAccepted) && (hasReviewedContract || client?.isExistingClient)) ||
                  (!isCounsellor && client?.currentCounsellorId && !client?.offboarded)
                ) && (
                  hasAppointments ? (
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleSessionNotes}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        py: 1.5,
                        backgroundColor: '#4caf50',
                        '&:hover': {
                          backgroundColor: '#388e3c'
                        }
                      }}
                    >
                      Session Notes
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => navigate(`/counsellor/appointments?clientId=${id}`)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        py: 1.5,
                        backgroundColor: '#2196f3',
                        '&:hover': {
                          backgroundColor: '#1976d2'
                        }
                      }}
                    >
                      Schedule Appointment
                    </Button>
                  )
                )}
              </Box>

              {/* Session History */}
              <PaymentSessionHistory clientId={client._id} />
            </Box>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && client && (
            <SimpleReviewHistory clientId={client._id} />
          )}

          {/* Audit Tab */}
          {activeTab === 'audit' && client && (
            <ClientAuditTimeline clientId={client._id} />
          )}
        </Box>

        {/* Mobile Menu Drawer */}
        <MenuDrawer
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          counsellorId={user?.counsellorId}
        />

        {/* Bottom Navigation */}
        <BottomNav counsellorId={user?.counsellorId} />
      </Box>

      {/* Desktop View */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
      <div className="container mx-auto px-2 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 overflow-y-scroll">
        <div className="glass-card p-3 sm:p-4 md:p-6 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto py-3 sm:py-4 md:py-6 px-2 sm:px-4 md:px-6 lg:px-8">
            {/* Header Section */}
            <div className="glass-card-light mb-4 sm:mb-6">
              <div className="max-w-7xl mx-auto py-3 sm:py-4 md:py-6 px-2 sm:px-4 md:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  {/* Buttons on the left */}
                  <div className="flex flex-col space-y-2 sm:space-y-4 w-full sm:w-auto">
                    {/* Primary Actions */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                      <Button
                        variant="contained"
                        onClick={() => navigate(isCounsellor ? '/counsellor/dashboard' : '/clients')}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          minWidth: { xs: 'auto', sm: '160px' },
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          px: { xs: 2, sm: 3 },
                          py: { xs: 1, sm: 1.5 },
                          backgroundColor: '#2196f3',
                          '&:hover': {
                            backgroundColor: '#1976d2'
                          }
                        }}
                      >
                        Back to {isCounsellor ? 'Dashboard' : 'Clients'}
                      </Button>
                    </div>
                  </div>

                  {/* Title on the right */}
                  <div className="text-left sm:text-right w-full sm:w-auto">
                    <Typography 
                      variant="h3" 
                      component="h1" 
                      gutterBottom
                      className="theme-text-primary"
                      sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 800,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                        letterSpacing: '0.5px'
                      }}
                    >
                      Client Details
                    </Typography>
                    {client && (
                      <Typography 
                        variant="subtitle1"
                        className="theme-text-secondary"
                        sx={{
                          fontWeight: 500,
                          fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' }
                        }}
                      >
                        {client.firstName} {client.lastName}
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Counsellor Assignment Status */}
            {client && (
              <div className="glass-card-light rounded-lg p-3 sm:p-4 md:p-6 mb-4 sm:mb-6">
                <Typography variant="h6" className="mb-2 theme-text-primary font-semibold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Counsellor Assignment
                </Typography>
                {client.assignedCounsellor || client.currentCounsellorId || (client.counsellorHistory && client.counsellorHistory.length > 0) ? (
                  <div>
                    <Typography variant="body1" className="theme-text-primary">
                      <span className="font-medium">Assigned to:</span> {client.assignedCounsellorName || 'Unknown'}
                    </Typography>
                    {client.counsellorHistory && client.counsellorHistory.length > 0 && (
                      <Typography variant="body2" className="theme-text-secondary mt-1">
                        Assigned on: {new Date(client.counsellorHistory[client.counsellorHistory.length - 1].assignedDate).toLocaleDateString()}
                      </Typography>
                    )}
                  </div>
                ) : (
                  <Typography variant="body1" className="text-orange-400 font-medium">
                    No counsellor assigned
                  </Typography>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-4 sm:mt-6 mb-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-4">
              {/* Review Contract Button - Counsellor only, visible if assigned to client */}
              {isAssignedCounsellor && client?.contractUrl && (
                <Button
                  variant="contained"
                  onClick={handleOpenContractReview}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    minWidth: '160px',
                    backgroundColor: hasReviewedContract ? '#9c27b0' : '#667eea',
                    '&:hover': {
                      backgroundColor: hasReviewedContract ? '#7b1fa2' : '#5568d3'
                    }
                  }}
                >
                  {hasReviewedContract ? 'View Contract' : 'Review Contract'}
                </Button>
              )}

              {/* Complete Onboarding Button - For counsellors with existing clients awaiting onboarding */}
              {isCounsellor && client?.isExistingClient && !client?.existingClientOnboarded && (
                <Button
                  variant="contained"
                  onClick={handleCompleteOnboardingClick}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    minWidth: '160px',
                    backgroundColor: '#f59e0b',
                    '&:hover': {
                      backgroundColor: '#d97706'
                    }
                  }}
                >
                  Complete Onboarding
                </Button>
              )}

              {/* Session Notes or Schedule Appointment Button - For Counsellors and Admins */}
              {(
                // Counsellors: require counsellor assignment and contract review
                (isCounsellor && client?.currentCounsellorId && !client?.offboarded && (client?.existingClientOnboarded || client?.termsAccepted) && (hasReviewedContract || client?.isExistingClient)) ||
                // Admins: only require client to have a counsellor and not be offboarded
                (!isCounsellor && client?.currentCounsellorId && !client?.offboarded)
              ) && (
                hasAppointments ? (
                  <Button
                    variant="contained"
                    onClick={handleSessionNotes}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minWidth: '160px',
                      backgroundColor: '#4caf50',
                      '&:hover': {
                        backgroundColor: '#388e3c'
                      }
                    }}
                  >
                    Session Notes
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => navigate(`/counsellor/appointments?clientId=${id}`)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minWidth: '160px',
                      backgroundColor: '#2196f3',
                      '&:hover': {
                        backgroundColor: '#1976d2'
                      }
                    }}
                  >
                    Schedule Appointment
                  </Button>
                )
              )}

              {/* Reassign Button - Admin only, visible if client has counsellor, not offboarded, and not pending_archive */}
              {!isCounsellor && (client?.assignedCounsellor || client?.currentCounsellorId || (client?.counsellorHistory && client?.counsellorHistory.length > 0)) && !client?.offboarded && client?.status !== 'pending_archive' && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => navigate(`/clients/${id}/allocate`)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    minWidth: '160px',
                    backgroundColor: '#2196f3',
                    '&:hover': {
                      backgroundColor: '#1976d2'
                    }
                  }}
                >
                  Reassign Client
                </Button>
              )}

              {/* Admin-only buttons */}
              {!isCounsellor && (
                <>
                  {!client?.assessed && !client?.offboarded && !client?.isExistingClient && (
                    <Button
                      variant="contained"
                      onClick={() => navigate(`/assessment-part-1/${id}`)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        backgroundColor: '#4caf50',
                        '&:hover': {
                          backgroundColor: '#388e3c'
                        }
                      }}
                    >
                      Start Assessment
                    </Button>
                  )}
                  {client?.assessed && !client?.termsAccepted && !client?.termsDeclined && !client?.offboarded && (
                    <Button
                      variant="contained"
                      onClick={handleReissueTerms}
                      disabled={isReissuingTerms}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        backgroundColor: '#9c27b0',
                        '&:hover': {
                          backgroundColor: '#7b1fa2'
                        }
                      }}
                    >
                      {isReissuingTerms ? 'Reissuing...' : 'Reissue Terms'}
                    </Button>
                  )}
                  {client?.assessed && client?.termsAccepted && !client?.currentCounsellorId && !client?.offboarded && (
                    <Button
                      variant="contained"
                      onClick={() => navigate(`/clients/${id}/allocate`)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        backgroundColor: '#2196f3',
                        '&:hover': {
                          backgroundColor: '#1976d2'
                        }
                      }}
                    >
                      Assign Counsellor
                    </Button>
                  )}
                  {client?.status === 'pending_archive' && !client?.offboarded && (
                    <Button
                      variant="contained"
                      onClick={handleReturnToCounsellor}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        backgroundColor: '#0d9488',
                        '&:hover': {
                          backgroundColor: '#0f766e'
                        }
                      }}
                    >
                      Return to Counsellor
                    </Button>
                  )}
                  {!client?.offboarded && (
                    <Button
                      variant="contained"
                      onClick={() => navigate(`/offboard/client/${id}`)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        backgroundColor: '#dc2626',
                        '&:hover': {
                          backgroundColor: '#b91c1c'
                        }
                      }}
                    >
                      Offboard Client
                    </Button>
                  )}
                  {/* Phase 6: admins may always open the recontract dialog -
                      ineligible clients require a recorded override reason,
                      enforced again server-side. Offboarded clients are not
                      recontracted (re-referral via reactivate first). */}
                  {!client?.offboarded && !client?.isDeleted && (
                    <Button
                      variant="contained"
                      onClick={handleOpenRecontract}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        backgroundColor: '#f59e0b',
                        '&:hover': {
                          backgroundColor: '#d97706'
                        }
                      }}
                    >
                      Recontract Client
                    </Button>
                  )}
                  {!client?.offboarded && !client?.isDeleted && (
                    <Button
                      variant="contained"
                      disabled={reviewInviteBusy}
                      onClick={handleReviewInvite}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        backgroundColor: '#7c3aed',
                        '&:hover': {
                          backgroundColor: '#6d28d9'
                        }
                      }}
                    >
                      {reviewInviteBusy ? 'Queuing…' : 'Review Invitation'}
                    </Button>
                  )}
                  {reviewInviteMsg && (
                    <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.85rem', marginTop: 8 }}>
                      {reviewInviteMsg}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Continuation Request Banner (counsellor flagged) */}
            {(user?.role === 'administrator' || client?.status !== 'pending_archive') && client?.continuationRequested !== undefined && client?.continuationRequestedAction && (
              <div className={`mb-4 p-4 rounded-lg border-2 ${
                client.continuationRequestedAction === 'recontract' 
                  ? 'border-blue-500/50 bg-blue-500/10' 
                  : 'border-orange-500/50 bg-orange-500/10'
              }`}>
                <Typography variant="body1" sx={{ 
                  color: client.continuationRequestedAction === 'recontract' ? '#3b82f6' : '#f97316', 
                  fontWeight: 700 
                }}>
                  {client.continuationRequestedAction === 'recontract' 
                    ? `📋 Counsellor has reported that ${client.firstName} wishes to continue self-pay. Please recontract this client.`
                    : `📋 Counsellor has reported that ${client.firstName} does not wish to continue. Please offboard this client.`
                  }
                </Typography>
                {client.continuationRequestedAt && (
                  <Typography variant="caption" className="theme-text-secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Flagged on {new Date(client.continuationRequestedAt).toLocaleDateString('en-GB')}
                  </Typography>
                )}
              </div>
            )}

            {/* Session Limit Warning Banner */}
            {(user?.role === 'administrator' || client?.status !== 'pending_archive') && client?.referralCategory === 'organisation' && client?.numberOfSessions && (() => {
              const completed = client.completedSessions || 0;
              const total = client.numberOfSessions;
              const remaining = total - completed;
              const counsellorFlaggedOffboard = client.continuationRequestedAction === 'offboard';
              
              if (remaining <= 0) {
                // Check if counsellor flagged client for offboarding
                if (counsellorFlaggedOffboard) {
                  return (
                    <div className="mb-4 p-4 rounded-lg border-2 border-red-500/50 bg-red-500/10">
                      <Typography variant="body1" sx={{ color: '#ef4444', fontWeight: 700 }}>
                        ⚠ Session Limit Reached — {completed} of {total} sessions completed. Client has indicated they do not wish to continue. Please offboard this client.
                      </Typography>
                    </div>
                  );
                } else {
                  return (
                    <div className="mb-4 p-4 rounded-lg border-2 border-red-500/50 bg-red-500/10">
                      <Typography variant="body1" sx={{ color: '#ef4444', fontWeight: 700 }}>
                        ⚠ Session Limit Reached — {completed} of {total} sessions completed. This client needs to be recontracted to continue counselling.
                      </Typography>
                    </div>
                  );
                }
              } else if (remaining <= 2) {
                return (
                  <div className="mb-4 p-4 rounded-lg border-2 border-amber-500/50 bg-amber-500/10">
                    <Typography variant="body1" sx={{ color: '#f59e0b', fontWeight: 600 }}>
                      ⚠ Approaching Session Limit — {completed} of {total} sessions completed ({remaining} remaining).
                    </Typography>
                  </div>
                );
              }
              return null;
            })()}

            {/* Desktop Tabs */}
            <div className="border-b border-white/20 mb-4 sm:mb-6">
              <Box sx={{ display: { xs: 'none', md: 'flex' }, overflowX: 'auto' }}>
                <button
                  className={`py-3 sm:py-4 px-2 sm:px-4 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                    activeTab === 'personal'
                      ? 'border-blue-400 text-blue-400'
                      : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                  }`}
                  onClick={() => setActiveTab('personal')}
                >
                  Personal Information
                </button>
                <button
                  className={`py-3 sm:py-4 px-2 sm:px-4 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                    activeTab === 'counselling'
                      ? 'border-blue-400 text-blue-400'
                      : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                  }`}
                  onClick={() => setActiveTab('counselling')}
                >
                  Counselling Information
                </button>
                <button
                  className={`py-3 sm:py-4 px-2 sm:px-4 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                    activeTab === 'payments'
                      ? 'border-blue-400 text-blue-400'
                      : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                  }`}
                  onClick={() => setActiveTab('payments')}
                >
                  Sessions
                </button>
                <button
                  className={`py-3 sm:py-4 px-2 sm:px-4 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                    activeTab === 'reviews'
                      ? 'border-blue-400 text-blue-400'
                      : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                  }`}
                  onClick={() => setActiveTab('reviews')}
                >
                  Reviews
                </button>
                <button
                  className={`py-3 sm:py-4 px-2 sm:px-4 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                    activeTab === 'audit'
                      ? 'border-blue-400 text-blue-400'
                      : 'border-transparent theme-text-secondary hover:text-white hover:border-white/30'
                  }`}
                  onClick={() => setActiveTab('audit')}
                >
                  Audit
                </button>
              </Box>
            </div>

            {/* Content */}
            <div>
              {activeTab === 'personal' && client && (
                <PersonalInformation
                  client={client}
                  lookups={lookups}
                  onUpdate={handleUpdateClient}
                  isEditing={isEditingPersonal}
                  setIsEditing={setIsEditingPersonal}
                  address={address}
                  setAddress={setAddress}
                  street={street}
                  setStreet={setStreet}
                  addressLine2={addressLine2}
                  setAddressLine2={setAddressLine2}
                  addressLine3={addressLine3}
                  setAddressLine3={setAddressLine3}
                  city={city}
                  setCity={setCity}
                  postcode={postcode}
                  setPostcode={setPostcode}
                  county={county}
                  setCounty={setCounty}
                  country={country}
                  setCountry={setCountry}
                  emergencyContactName={emergencyContactName}
                  setEmergencyContactName={setEmergencyContactName}
                  emergencyContactPhone={emergencyContactPhone}
                  setEmergencyContactPhone={setEmergencyContactPhone}
                  emergencyContactRelationship={emergencyContactRelationship}
                  setEmergencyContactRelationship={setEmergencyContactRelationship}
                  emergencyContacts={emergencyContacts}
                  setEmergencyContacts={setEmergencyContacts}
                  gpPractice={gpPractice}
                  setGpPractice={setGpPractice}
                  gpAddress={gpAddress}
                  setGpAddress={setGpAddress}
                  gpAddressLine2={gpAddressLine2}
                  setGpAddressLine2={setGpAddressLine2}
                  gpAddressLine3={gpAddressLine3}
                  setGpAddressLine3={setGpAddressLine3}
                  gpCity={gpCity}
                  setGpCity={setGpCity}
                  gpCounty={gpCounty}
                  setGpCounty={setGpCounty}
                  gpPostcode={gpPostcode}
                  setGpPostcode={setGpPostcode}
                  gpCountry={gpCountry}
                  setGpCountry={setGpCountry}
                  gpPhone={gpPhone}
                  setGpPhone={setGpPhone}
                  gender={gender}
                  setGender={setGender}
                  selectedAvailability={selectedAvailability}
                  setSelectedAvailability={setSelectedAvailability}
                  availabilitySlots={availabilitySlots}
                  setAvailabilitySlots={setAvailabilitySlots}
                  selectedSessionTypes={selectedSessionTypes}
                  setSelectedSessionTypes={setSelectedSessionTypes}
                />
              )}
              {activeTab === 'counselling' && (
                <>
                  <CounsellingInformation
                    client={client}
                    episodes={episodes}
                    episodesLoading={episodesLoading}
                    episodesError={episodesError}
                    createEpisode={createEpisode}
                  />
                  
                  {/* Historical Notes Control - Admin Only */}
                  {!isCounsellor && (
                    <HistoricalNotesControl
                      clientId={client._id}
                      allowHistoricalNotes={client.allowHistoricalNotes || false}
                      historicalNotesDeadline={client.historicalNotesDeadline}
                      historicalNotesCompletedFor={client.historicalNotesCompletedFor}
                      onUpdate={() => setRetryCount(prev => prev + 1)}
                    />
                  )}
                </>
              )}
              {activeTab === 'payments' && client && (
                <PaymentSessionHistory clientId={client._id} />
              )}
              {activeTab === 'reviews' && client && (
                <SimpleReviewHistory clientId={client._id} />
              )}
              {activeTab === 'audit' && client && (
                <ClientAuditTimeline clientId={client._id} />
              )}
            </div>
          </div>
        </div>
      </div>
      </Box>
      </>
    );
  };

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-6 py-8 overflow-y-scroll">
        {renderContent()}
      </div>

      {/* Contract Review Dialog */}
      <Dialog
        open={isReviewingContract}
        onClose={hasReviewedContract ? handleCloseContractReview : undefined}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pb: 2
        }}>
          <Typography variant="h5" className="theme-text-primary" sx={{ fontWeight: 700 }}>
            {hasReviewedContract ? 'Client Contract' : 'Review Client Contract'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {client && client.contractUrl ? (
            <Box>
              <Typography className="theme-text-secondary" sx={{ mb: 3 }}>
                {hasReviewedContract 
                  ? 'This is the contract that the client has signed. You have already acknowledged this contract.'
                  : 'Please review the client\'s contract below. You must acknowledge that you have read and understood the contract terms before you can access session notes and begin working with this client.'}
              </Typography>

              <Box sx={{
                p: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <Typography className="theme-text-primary" sx={{ mb: 2, fontWeight: 600 }}>
                  Contract Document
                </Typography>
                <Button
                  variant="contained"
                  onClick={async () => {
                    try {
                      const response = await api.get(`/api/contract/get-presigned-url/${id}`);
                      window.open(response.data.presignedUrl, '_blank', 'noopener,noreferrer');
                    } catch (error) {
                      console.error('Error getting contract URL:', error);
                      setSnackbar({
                        open: true,
                        message: 'Error opening contract',
                        severity: 'error'
                      });
                    }
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#2196f3',
                    '&:hover': {
                      backgroundColor: '#1976d2'
                    }
                  }}
                >
                  Open Contract in New Tab
                </Button>
                <Typography className="theme-text-secondary" sx={{ mt: 2, fontSize: '0.9rem' }}>
                  The contract will open in a new browser tab for your review.
                </Typography>
              </Box>

              {!hasReviewedContract && (
                <Box sx={{
                  p: 2,
                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(102, 126, 234, 0.3)',
                  mt: 2
                }}>
                  <Typography className="theme-text-primary" sx={{ fontWeight: 600, mb: 1 }}>
                    Important:
                  </Typography>
                  <Typography className="theme-text-secondary" sx={{ fontSize: '0.9rem' }}>
                    By clicking "I Acknowledge" below, you confirm that you have read and understood the terms of this client's contract and agree to provide services in accordance with these terms.
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography className="theme-text-secondary">
              No contract available for this client.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <div className="flex justify-end gap-3 w-full">
            <Button
              onClick={handleCloseContractReview}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
              variant="outlined"
            >
              {hasReviewedContract ? 'Close' : 'Cancel'}
            </Button>
            {!hasReviewedContract && (
              <Button
                onClick={handleAcknowledgeContract}
                variant="contained"
                disabled={isSavingContract}
                sx={{
                  backgroundColor: '#667eea',
                  '&:hover': {
                    backgroundColor: '#5568d3'
                  }
                }}
              >
                {isSavingContract ? 'Saving...' : 'I Acknowledge'}
              </Button>
            )}
          </div>
        </DialogActions>
      </Dialog>

      {/* Recontract Dialog */}
      <Dialog
        open={isRecontractOpen}
        onClose={() => !isRecontracting && setIsRecontractOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', pb: 2 }}>
          <Typography variant="h5" className="theme-text-primary" sx={{ fontWeight: 700 }}>
            Recontract Client
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography className="theme-text-secondary" sx={{ mb: 2 }}>
              Recontract <strong>{client?.firstName} {client?.lastName}</strong> to continue their counselling under a new contract.
              {' '}The current contract will be archived (with its final financial position) and the client will need a new assessment and contract.
            </Typography>
            {client?.contractHistory && client.contractHistory.length > 0 && (
              <Typography className="theme-text-secondary" sx={{ mb: 2, fontSize: '0.85rem', fontStyle: 'italic' }}>
                This client has been recontracted {client.contractHistory.length} time(s) previously.
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography className="theme-text-primary" sx={{ fontWeight: 600, mb: 1 }}>
              Referral Category *
            </Typography>
            <select
              value={recontractCategory}
              onChange={(e) => {
                setRecontractCategory(e.target.value as 'direct' | 'organisation');
                setSelectedSessionRate('');
                setSelectedOrganisation('');
                setSelectedReferralType('');
              }}
              className="theme-input w-full"
              style={{ padding: '10px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              <option value="direct">Direct / Self-Pay</option>
              <option value="organisation">Organisation Referral</option>
            </select>
          </Box>

          {recontractCategory === 'direct' ? (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography className="theme-text-primary" sx={{ fontWeight: 600, mb: 1 }}>
                  Session Rate *
                </Typography>
                <select
                  value={selectedSessionRate}
                  onChange={(e) => setSelectedSessionRate(e.target.value)}
                  className="theme-input w-full"
                  style={{ padding: '10px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
                >
                  <option value="">Select a session rate</option>
                  {sessionRates.map((rate) => (
                    <option key={rate._id} value={rate.value}>
                      {rate.displayName}
                    </option>
                  ))}
                </select>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography className="theme-text-primary" sx={{ fontWeight: 600, mb: 1 }}>
                  Referral Type
                </Typography>
                <select
                  value={selectedReferralType}
                  onChange={(e) => setSelectedReferralType(e.target.value)}
                  className="theme-input w-full"
                  style={{ padding: '10px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
                >
                  <option value="direct">Direct</option>
                  {referralTypes.map((type) => (
                    <option key={type._id} value={type.value}>
                      {type.displayName}
                    </option>
                  ))}
                </select>
              </Box>
            </>
          ) : (
            <Box sx={{ mb: 3 }}>
              <Typography className="theme-text-primary" sx={{ fontWeight: 600, mb: 1 }}>
                Organisation *
              </Typography>
              <select
                value={selectedOrganisation}
                onChange={(e) => setSelectedOrganisation(e.target.value)}
                className="theme-input w-full"
                style={{ padding: '10px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                <option value="">Select an organisation</option>
                {organisations.map((org) => (
                  <option key={org._id} value={org._id}>
                    {org.displayName}
                  </option>
                ))}
              </select>
              {selectedOrganisation && (() => {
                const org = organisations.find(o => o._id === selectedOrganisation);
                return org ? (
                  <Typography className="theme-text-secondary" sx={{ mt: 1, fontSize: '0.85rem' }}>
                    Session rate: £{org.sessionRate || 0} · Sessions: {org.numberOfSessions || 'N/A'}
                  </Typography>
                ) : null;
              })()}
            </Box>
          )}

          {!recontractAutoEligible && (
            <Box sx={{ mb: 3 }}>
              <Typography className="theme-text-primary" sx={{ fontWeight: 600, mb: 1 }}>
                Administrator Override Reason *
              </Typography>
              <Typography className="theme-text-secondary" sx={{ mb: 1, fontSize: '0.85rem' }}>
                This client is not automatically eligible for recontracting (allocated sessions are not
                exhausted and no counsellor continuation recommendation is recorded). An override reason
                is required and will be recorded.
              </Typography>
              <textarea
                value={recontractOverrideReason}
                onChange={(e) => setRecontractOverrideReason(e.target.value)}
                rows={2}
                className="theme-input w-full"
                style={{ padding: '10px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
                placeholder="e.g. Clinical need identified at review; client returning to complete treatment plan"
              />
            </Box>
          )}

          <Box sx={{
            p: 2,
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(245, 158, 11, 0.3)'
          }}>
            <Typography sx={{ color: '#f59e0b', fontWeight: 600, mb: 1 }}>
              What will happen:
            </Typography>
            <Typography className="theme-text-secondary" sx={{ fontSize: '0.85rem' }}>
              • Current contract will be archived in contract history<br />
              • The final financial position (including any outstanding balance) is preserved<br />
              • Assessment and contract pipeline will be reset<br />
              • Existing session notes will be preserved<br />
              • Client will need a new assessment and must sign a new contract
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <div className="flex justify-end gap-3 w-full">
            <Button
              onClick={() => setIsRecontractOpen(false)}
              disabled={isRecontracting}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRecontract}
              variant="contained"
              disabled={isRecontracting || (recontractCategory === 'direct' ? !selectedSessionRate : !selectedOrganisation)}
              sx={{
                backgroundColor: '#f59e0b',
                '&:hover': { backgroundColor: '#d97706' },
                '&:disabled': { backgroundColor: 'rgba(245, 158, 11, 0.3)' }
              }}
            >
              {isRecontracting ? 'Recontracting...' : 'Confirm Recontract'}
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      {/* Client Active Status Dialog */}
      <Dialog
        open={clientActiveDialog}
        onClose={() => setClientActiveDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1e3a8a',
            backgroundImage: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
            color: 'white',
            minWidth: { xs: '90%', sm: '500px' }
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Client Status Confirmation
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ mb: 2 }}>
            Is <strong>{client?.firstName} {client?.lastName}</strong> still an active client?
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            If the client is still active, you can proceed with completing their onboarding.
            If they are no longer active, we'll help you archive their record.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <div className="flex justify-end gap-3 w-full">
            <Button
              onClick={handleClientNotActive}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderColor: 'rgba(255, 255, 255, 0.5)'
                }
              }}
              variant="outlined"
            >
              No, Not Active
            </Button>
            <Button
              onClick={handleClientStillActive}
              variant="contained"
              sx={{
                backgroundColor: '#10b981',
                '&:hover': { backgroundColor: '#059669' }
              }}
            >
              Yes, Still Active
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      {/* Inactive Client Dialog */}
      <Dialog
        open={inactiveClientDialog}
        onClose={() => !isArchivingClient && setInactiveClientDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1e3a8a',
            backgroundImage: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
            color: 'white',
            minWidth: { xs: '90%', sm: '500px' }
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Archive Inactive Client
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ mb: 3 }}>
            When did <strong>{client?.firstName} {client?.lastName}</strong> stop being an active client?
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
              Date Client Left *
            </Typography>
            <input
              type="date"
              value={clientLeftDate}
              onChange={(e) => setClientLeftDate(e.target.value)}
              max={todayDateOnlyString()}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '6px',
                backgroundColor: 'rgba(255,255,255,0.05)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.2)',
                fontSize: '1rem'
              }}
            />
          </Box>

          <Box sx={{
            p: 2,
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(245, 158, 11, 0.3)'
          }}>
            <Typography sx={{ color: '#f59e0b', fontWeight: 600, mb: 1 }}>
              What will happen:
            </Typography>
            <Typography className="theme-text-secondary" sx={{ fontSize: '0.85rem' }}>
              • Client status will be set to "Pending Archive"<br />
              • Client will be removed from your client list<br />
              • Administrators will be notified<br />
              • All client data will be preserved for records
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <div className="flex justify-end gap-3 w-full">
            <Button
              onClick={() => setInactiveClientDialog(false)}
              disabled={isArchivingClient}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleArchiveInactiveClient}
              variant="contained"
              disabled={isArchivingClient || !clientLeftDate}
              sx={{
                backgroundColor: '#ef4444',
                '&:hover': { backgroundColor: '#dc2626' },
                '&:disabled': { backgroundColor: 'rgba(239, 68, 68, 0.3)' }
              }}
            >
              {isArchivingClient ? 'Archiving...' : 'Archive Client'}
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeBackground>
  );
};

export default ClientDetails;